import { ThemeEvents } from '@theme/events';
import { prefersReducedMotion } from '@theme/utilities';

/**
 * Autoplay for the product gallery's uploaded videos (THEME.md §7.9 **Gallery**, §9.4 #35; D-112, T-179; DESIGN.md ›
 * Components › Product gallery). `snippets/okt-video-autoplay.liquid` renders the element right after a video's
 * `<deferred-media>` while the gallery's Autoplay is on. Horizon's poster and template stay (product-media passes
 * `okt_in_view`), so nothing loads until the video shows: half of it on screen (half the screen for one bigger than the
 * screen), which a carousel's track clips to the current slide, not inert ("Show more" keeps the collapsed media inert),
 * not in a closed dialog and not under an open modal one (the zoom, quick add, a drawer). Then Horizon's `loadContent`
 * adds the video and it plays, muted and inline; it pauses when it stops showing and plays again when it shows.
 * Horizon's play/pause toggle stays the control, and a pause there sticks, across variant changes too. These starts
 * aren't announced, so autoplaying videos don't pause each other; a video the visitor starts still pauses the rest, as
 * in Horizon, until they stop showing. Under reduced motion or with Save-Data nothing changes: Horizon's poster and
 * play button, as on the banner video (T-175).
 *
 * @typedef {HTMLElement & {
 *   isPlaying: boolean,
 *   loadContent: (focus?: boolean) => void,
 *   pauseMedia: () => void,
 *   updatePlayPauseHint: (isPlaying: boolean) => void
 * }} DeferredMedia
 */

/** @type {WeakMap<Element, OktVideoAutoplay>} */
const players = new WeakMap();

/** @type {Set<OktVideoAutoplay>} */
const active = new Set();

/**
 * The videos the visitor paused, by media id: the pause outlives the gallery, which Horizon replaces on every variant
 * change, and holds for the video's other copies (the carousel, the grid, the zoom)
 * @type {Set<string | Element>}
 */
const pausedByVisitor = new Set();

const viewport = new IntersectionObserver(
  (entries) => {
    for (const entry of entries) players.get(entry.target)?.update(onScreen(entry));
  },
  { threshold: Array.from({ length: 21 }, (_, step) => step / 20) }
);

/**
 * Half of the video on screen, or half the screen for a video bigger than it (the zoom's, full width).
 * @param {IntersectionObserverEntry} entry
 */
function onScreen({ intersectionRect: seen, boundingClientRect: box, rootBounds }) {
  const area = seen.width * seen.height;
  const screen = rootBounds ? rootBounds.width * rootBounds.height : innerWidth * innerHeight;

  return area > 0 && area >= 0.5 * Math.min(box.width * box.height, screen);
}

// What hides or shows a video without moving it: "Show more" (inert) and dialogs (open)
new MutationObserver(() => {
  for (const player of active) player.update();
}).observe(document.documentElement, { attributes: true, attributeFilter: ['inert', 'open'], subtree: true });

class OktVideoAutoplay extends HTMLElement {
  /** @type {DeferredMedia | null} */
  #media = null;

  /** @type {AbortController | null} */
  #controller = null;

  /** Enough of it on screen, from the IntersectionObserver */
  #seen = false;

  /** Whether the video showed when last checked */
  #shown = false;

  /** @type {string | Element} The media's id (product-media's data-media-id), else this element */
  #id = this;

  /** loadContent's start announcement is ours */
  #starting = false;

  connectedCallback() {
    const media = this.previousElementSibling;
    if (media?.localName !== 'deferred-media') return;
    if (prefersReducedMotion() || /** @type {any} */ (navigator).connection?.saveData) return;

    const controller = new AbortController();
    const { signal } = controller;
    this.#controller = controller;
    this.#id = this.closest('[data-media-id]')?.getAttribute('data-media-id') ?? this;

    customElements.whenDefined('deferred-media').then(() => {
      if (signal.aborted) return;
      const deferred = /** @type {DeferredMedia} */ (media);

      // Our start stays here: Horizon's document listener would pause every other video, the autoplaying ones too
      media.addEventListener(
        ThemeEvents.mediaStartedPlaying,
        (event) => {
          if (this.#starting) event.stopPropagation();
        },
        { signal }
      );

      // A pause with the toggle sticks until the visitor plays it again, there or with the poster. Horizon's handlers
      // have run by now: they listen on the document while capturing.
      media.addEventListener(
        'click',
        (event) => {
          const buttons = '[ref="toggleMediaButton"], [ref="deferredMediaPlayButton"]';
          if (!(event.target instanceof Element) || !event.target.closest(buttons)) return;
          if (deferred.isPlaying) pausedByVisitor.delete(this.#id);
          else pausedByVisitor.add(this.#id);
        },
        { signal }
      );

      // Without Video looping it stops at its end, and the toggle offers to play it again
      media.addEventListener('ended', () => deferred.updatePlayPauseHint((deferred.isPlaying = false)), {
        capture: true,
        signal,
      });

      this.#media = deferred;
      players.set(media, this);
      active.add(this);
      viewport.observe(media);
    });
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#controller = null;
    active.delete(this);
    if (this.#media) viewport.unobserve(this.#media);
    this.#media = null;
    this.#seen = false;
    this.#shown = false;
  }

  /**
   * Plays the video when it starts showing and pauses it when it stops.
   * @param {boolean} [seen] - The IntersectionObserver's verdict, when it has a new one
   */
  update(seen = this.#seen) {
    const media = this.#media;
    if (!media) return;

    this.#seen = seen;
    const shown = seen && !media.closest('[inert], dialog:not([open])') && !covered(media);
    if (shown === this.#shown) return;
    this.#shown = shown;

    if (!shown) {
      if (media.hasAttribute('data-media-loaded')) media.pauseMedia();
    } else if (!pausedByVisitor.has(this.#id)) {
      this.#play(media);
    }
  }

  /** @param {DeferredMedia} media */
  #play(media) {
    media.dataset.oktAutoplay = '';

    if (!media.hasAttribute('data-media-loaded')) {
      this.#starting = true;
      try {
        media.loadContent(false);
      } finally {
        this.#starting = false;
      }
    }

    const video = media.querySelector('video');
    if (!video) return;

    // A clone of the template's video doesn't always keep its muted attribute, and only muted video may start alone
    video.muted = true;
    video.play().catch(() => media.updatePlayPauseHint((media.isPlaying = false)));
    media.updatePlayPauseHint((media.isPlaying = true));
  }
}

/**
 * An open modal dialog that the video isn't in covers it.
 * @param {Element} media
 */
function covered(media) {
  for (const dialog of document.querySelectorAll('dialog[open]')) {
    if (!dialog.contains(media) && dialog.matches(':modal')) return true;
  }
  return false;
}

if (!customElements.get('okt-video-autoplay')) customElements.define('okt-video-autoplay', OktVideoAutoplay);
